import type { GraphQLError } from 'graphql';

import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { expVal } from '@confluence/feature-experiments';
import {
	PageTitleQuery,
	type PageTitleQuery$data,
	type PageTitleQuery$variables,
} from '@confluence/content-title/entry-points/PageTitleQuery.graphql.ts';

import FolderOverviewFirstPageQueryNode, {
	type FolderOverviewFirstPageQuery$data,
	type FolderOverviewFirstPageQuery$variables,
} from './__generated__/FolderOverviewFirstPageQuery.graphql.ts';
import FolderRedirectFirstChildQueryNode, {
	type FolderRedirectFirstChildQuery$data,
	type FolderRedirectFirstChildQuery$variables,
} from './FolderLanding/__generated__/FolderRedirectFirstChildQuery.graphql.ts';
import DualStreamQueriesFirstPageQueryNode, {
	type DualStreamQueriesFirstPageQuery$data,
	type DualStreamQueriesFirstPageQuery$variables,
} from './__generated__/DualStreamQueriesFirstPageQuery.graphql.ts';

/**
 * Number of items to overfetch per stream on the initial dual-stream load.
 * MUST stay in sync with `FETCH_SIZE` in `useDualStreamPagination.ts` — the
 * preload variables have to match the component's query variables exactly, or
 * the SSR-warmed store snapshot won't be treated as complete for the
 * component's operation (and SSR would render empty content).
 */
const DUAL_STREAM_FETCH_SIZE = 100;

type PreloadFolderDataOptions = {
	/** Cloud ID — required by the dual-stream content query. */
	cloudId?: string;
	/** Space key — required by the dual-stream content query. */
	spaceKey?: string;
};

export async function preloadFolderData(
	contentId: string,
	{ cloudId, spaceKey }: PreloadFolderDataOptions = {},
): Promise<
	| void
	| Error[]
	// `data` is intentionally `unknown` because this preloader batches several
	// different queries (dual-stream vs legacy overview, redirect first-child,
	// and page title) whose result shapes differ. Consumers only care about the
	// error/`hasErrors` fields, not the specific `data` payload.
	| {
			data: unknown;
			errors?: ReadonlyArray<GraphQLError>;
			hasErrors: boolean;
	  }[]
> {
	// The render path branches on `cc_folder_view_type_filtered_children`
	// (see FolderOverview.tsx): the dual-stream path fires
	// `DualStreamQueriesFirstPageQuery` while the legacy path fires
	// `FolderOverviewFirstPageQuery`. Preload whichever one the render will
	// actually consume so its data is in the store before the first SSR render.
	const overviewQueryTask: Promise<{
		data: unknown;
		errors?: ReadonlyArray<GraphQLError>;
		hasErrors: boolean;
	}> = expVal('cc_folder_view_type_filtered_children', 'isEnabled', false)
		? query<DualStreamQueriesFirstPageQuery$data, DualStreamQueriesFirstPageQuery$variables>({
				query: DualStreamQueriesFirstPageQueryNode,
				// These variables MUST match the ones passed by `useDualStreamPagination`
				// (empty-string fallbacks for cloudId/spaceKey included) so the store
				// snapshot is complete for the component's operation on SSR.
				variables: {
					cloudId: cloudId ?? '',
					parentId: contentId,
					spaceKey: spaceKey ?? '',
					contentId,
					first: DUAL_STREAM_FETCH_SIZE,
					includeDevDocsSyncStatus: expVal('confluence_dev_docs', 'isEnabled', false),
				},
			})
		: query<FolderOverviewFirstPageQuery$data, FolderOverviewFirstPageQuery$variables>({
				query: FolderOverviewFirstPageQueryNode,
				variables: {
					id: contentId,
					after: null,
					contentId,
					includeDevDocsSyncStatus: expVal('confluence_dev_docs', 'isEnabled', false),
				},
			});

	return waitForAllReturnWhenError([
		overviewQueryTask,
		// Always preload the redirect query — the folder landing route
		// (`confluence_folder_view` OFF) renders `FolderRedirect`, which consumes it.
		query<FolderRedirectFirstChildQuery$data, FolderRedirectFirstChildQuery$variables>({
			query: FolderRedirectFirstChildQueryNode,
			variables: { id: contentId },
		}),
		// Preload the folder header title. `FolderHeader` → `FolderInlineTitle`
		// consumes it via `usePageTitle`, which only fetches on the client; without
		// this the folder title renders empty during SSR.
		query<PageTitleQuery$data, PageTitleQuery$variables>({
			query: PageTitleQuery,
			variables: { contentId },
		}),
	]);
}
